import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import DriverShell from "../components/DriverShell";
import { api, authenticatedApi } from "../lib/api";
import { getDriverSession } from "../lib/driverSession";

const ASSIGNED_FOOD_CACHE = "dh_driver_assigned_food_v1";
const FOOD_QUEUE_CACHE = "dh_driver_food_queue_v2";
const SERVICE_QUEUE_CACHE = "dh_driver_service_queue_v1";

function rememberAssignedFood(orderId) {
  const numericId = Number(orderId);
  if (!Number.isFinite(numericId)) return;
  try {
    const current = JSON.parse(localStorage.getItem(ASSIGNED_FOOD_CACHE) || "[]").map(Number);
    localStorage.setItem(ASSIGNED_FOOD_CACHE, JSON.stringify(Array.from(new Set([...current, numericId]))));
  } catch {
    localStorage.setItem(ASSIGNED_FOOD_CACHE, JSON.stringify([numericId]));
  }
}

function money(value) {
  return `S/ ${Number(value || 0).toFixed(2)}`;
}

function cleanLine(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function buildShareMessage(order, isFood, reference, mapsUrl, amounts) {
  const lines = [
    `*PEDIDO ${cleanLine(reference)} - DELIVERY HUARAZ*`,
    "",
  ];
  if (isFood) {
    lines.push(`*Negocio:* ${cleanLine(order.restaurant_contact?.name || order.restaurant?.name)}`);
    if (order.restaurant_contact?.phone) lines.push(`*Tel. negocio:* ${cleanLine(order.restaurant_contact.phone)}`);
    lines.push(`*Recojo:* ${cleanLine(order.restaurant?.address)}`);
    lines.push(`*Cliente:* ${cleanLine(order.user?.name)}`);
    if (order.user?.phone) lines.push(`*Tel. cliente:* ${cleanLine(order.user.phone)}`);
    lines.push(`*Entrega:* ${cleanLine(order.address)}`);
    if (order.landmark) lines.push(`*Referencia:* ${cleanLine(order.landmark)}`);
    lines.push("", "*Productos:*");
    (order.orderitems || []).forEach((item) => {
      lines.push(`• ${Number(item.quantity || 1)}x ${cleanLine(item.name)} — ${money(Number(item.price || 0) * Number(item.quantity || 1))}`);
      (item.order_item_addons || []).forEach((addon) => {
        lines.push(`  + ${cleanLine(addon.addon_name)} — ${money(Number(addon.addon_price ?? addon.price ?? 0) * Number(item.quantity || 1))}`);
      });
    });
    if (order.order_comment) lines.push("", `*Indicación:* ${cleanLine(order.order_comment)}`);
    lines.push(
      "",
      `*Productos:* ${money(amounts.products)}`,
      `*Delivery:* ${money(amounts.delivery)}`,
      `*Total a cobrar:* ${money(amounts.collect)}`,
      `*Forma de pago:* ${cleanLine(order.payment_display || order.payment_mode)}`,
    );
  } else {
    lines.push(`*Tipo:* ${cleanLine(order.order_type_label)}`);
    lines.push(`*Recojo:* ${cleanLine(order.pickup_address)}`);
    if (order.pickup_reference) lines.push(`*Referencia de recojo:* ${cleanLine(order.pickup_reference)}`);
    lines.push(`*Contacto de recojo:* ${cleanLine(order.pickup_contact_name)} ${cleanLine(order.pickup_contact_phone)}`);
    lines.push(`*Entrega:* ${cleanLine(order.dropoff_address)}`);
    if (order.dropoff_reference) lines.push(`*Referencia de entrega:* ${cleanLine(order.dropoff_reference)}`);
    lines.push(`*Contacto de entrega:* ${cleanLine(order.dropoff_contact_name)} ${cleanLine(order.dropoff_contact_phone)}`);
    if (order.package_description) lines.push(`*Encargo:* ${cleanLine(order.package_description)}`);
    if (order.customer_notes) lines.push(`*Indicación:* ${cleanLine(order.customer_notes)}`);
    lines.push(`*Total:* ${money(order.total)}`);
  }
  lines.push("", `*GPS y ruta:* ${mapsUrl}`, "", "Pedido asignado al motorizado titular. No incluye el PIN de entrega.");
  return lines.filter((line) => line !== undefined).join("\n");
}

function coordinatesFrom(value) {
  if (!value) return null;
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return null;
    }
  }
  const lat = Number(parsed?.lat ?? parsed?.latitude);
  const lng = Number(parsed?.lng ?? parsed?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0 ? { lat, lng } : null;
}

function Contact({ title, name, phone, icon = "●" }) {
  if (!name && !phone) return null;
  const whatsapp = String(phone || "").replace(/\D/g, "");
  return (
    <section className="driver-detail-card driver-contact-card">
      <div className="driver-contact-card__heading"><i>{icon}</i><div><small>{title}</small><h2>{name || "Contacto"}</h2></div></div>
      {phone && <div className="driver-contact-actions"><a href={`tel:${phone}`}>📞 Llamar</a><a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer">WhatsApp</a></div>}
    </section>
  );
}

function OrderActionButton({ label, onComplete, disabled = false, tone = "orange" }) {
  const [running, setRunning] = useState(false);
  async function run() {
    if (disabled || running) return;
    if (!window.confirm(`¿Confirmas la acción “${label}”?`)) return;
    setRunning(true);
    const successful = await onComplete();
    if (!successful) setRunning(false);
  }
  return (
    <button className={`driver-order-action driver-order-action--${tone}`} disabled={disabled || running} onClick={run} type="button">
      <span>{running ? "Procesando…" : label}</span><b aria-hidden="true">✓</b>
    </button>
  );
}

export default function DriverOrderDetailPage() {
  const { reference } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const driver = getDriverSession();
  const kind = params.get("type") || "food";
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pin, setPin] = useState("");
  const [clock, setClock] = useState(Date.now());
  const loadInFlight = useRef(false);
  const hasLoadedOrder = useRef(false);

  function cachePickedUp(currentOrder) {
    const cacheKey = kind === "food" ? FOOD_QUEUE_CACHE : SERVICE_QUEUE_CACHE;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
      if (!cached || Number(cached.driverId) !== Number(driver.id)) return;
      const payload = cached.payload || {};
      const sameOrder = (item) => Number(item.id) === Number(currentOrder.id);
      const picked = { ...currentOrder, ...(kind === "food" ? { orderstatus_id: 4 } : { status: "PICKED_UP" }) };
      cached.payload = {
        ...payload,
        new_orders: (payload.new_orders || []).filter((item) => !sameOrder(item)),
        accepted_orders: (payload.accepted_orders || []).filter((item) => !sameOrder(item)),
        pickedup_orders: [picked, ...(payload.pickedup_orders || []).filter((item) => !sameOrder(item))],
      };
      cached.savedAt = Date.now();
      localStorage.setItem(cacheKey, JSON.stringify(cached));
    } catch {
      // La consulta del servidor reconstruirá la cola si no existe caché.
    }
  }

  async function load() {
    if (!driver?.auth_token || loadInFlight.current) return;
    loadInFlight.current = true;
    try {
      if (kind === "food") {
        const response = await authenticatedApi("/delivery/get-single-delivery-order", driver.auth_token, {
          method: "POST",
          body: JSON.stringify({ unique_order_id: reference }),
          retry: 2,
        });
        setOrder(response);
        if (response?.delivery_assigned_to_me) rememberAssignedFood(response.id);
      } else {
        const response = await authenticatedApi("/delivery/service-orders", driver.auth_token, { method: "POST", body: "{}" });
        const all = [...(response.new_orders || []), ...(response.accepted_orders || []), ...(response.pickedup_orders || []), ...(response.completed_orders || [])];
        const found = all.find((entry) => entry.reference === reference);
        if (!found) throw new Error("Este servicio ya no está disponible.");
        setOrder(found);
      }
      hasLoadedOrder.current = true;
      setError("");
    } catch (requestError) {
      if (!hasLoadedOrder.current || !requestError.isNetworkError) setError(requestError.message);
    } finally {
      loadInFlight.current = false;
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    api("/get-settings", { method: "POST", body: "{}" }).then((items) => {
      setSettings(Object.fromEntries((items || []).map((item) => [item.key, item.value])));
    }).catch(() => {});
  }, [driver?.auth_token, kind, reference]);

  useEffect(() => {
    if (!driver?.auth_token || !order || [5, 6].includes(Number(order.orderstatus_id))) return undefined;
    const refresh = () => load();
    const timer = window.setInterval(refresh, 5000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [driver?.auth_token, kind, reference, order?.id, order?.orderstatus_id]);

  useEffect(() => {
    if (!order?.flow_v1?.waiting_store_acceptance) return undefined;
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [order?.flow_v1?.waiting_store_acceptance]);

  async function act(path, body = {}, alreadyConfirmed = false) {
    if (!alreadyConfirmed && !window.confirm("¿Confirmas esta acción? El estado del servicio se actualizará para todos.")) return false;
    setBusy(true);
    setError("");
    try {
      const response = await authenticatedApi(path, driver.auth_token, { method: "POST", body: JSON.stringify(body) });
      if (response?.delivery_pin_error) {
        throw new Error("El PIN de entrega no es correcto. Verifícalo con el cliente e inténtalo nuevamente.");
      }
      if (path === "/delivery/deliver-order" || /\/delivery\/service-orders\/\d+\/deliver$/.test(path)) {
        navigate(`/delivery/completed-orders?delivered=${encodeURIComponent(reference)}`, { replace: true });
        return true;
      }
      if (path === "/delivery/pickedup-order" || /\/delivery\/service-orders\/\d+\/pickup$/.test(path)) {
        cachePickedUp(order);
        navigate("/delivery/orders?tab=pickedup", { replace: true });
        return true;
      }
      await load();
      return true;
    } catch (requestError) {
      setError(requestError.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function releaseReservation() {
    setBusy(true);
    setError("");
    try {
      await authenticatedApi("/delivery/release-reservation", driver.auth_token, {
        method: "POST",
        body: JSON.stringify({ order_id: order.id, reason: "El negocio demoró en aceptar." }),
      });
      navigate("/delivery/orders", { replace: true });
      return true;
    } catch (requestError) {
      setError(requestError.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  const isFood = kind === "food";
  const customerGps = order
    ? (isFood ? coordinatesFrom(order.location) : coordinatesFrom({ lat: order.dropoff_latitude, lng: order.dropoff_longitude }))
    : null;
  const storeGps = order && isFood ? coordinatesFrom(order.restaurant) : null;
  const destination = customerGps ? `${customerGps.lat},${customerGps.lng}` : (isFood ? order?.address : order?.dropoff_address);
  const mapsUrl = order
    ? `https://www.google.com/maps/dir/?api=1${storeGps ? `&origin=${encodeURIComponent(`${storeGps.lat},${storeGps.lng}`)}` : ""}&destination=${encodeURIComponent(destination || "")}`
    : "#";
  const collectAmount = Number(
    order?.payment_breakdown?.customer_collect
      ?? order?.payment_breakdown?.customer_collect_total
      ?? order?.payable
      ?? order?.total
      ?? 0,
  );
  const deliveryAmount = Number(
    order?.actual_delivery_charge
      ?? order?.delivery_charge
      ?? order?.payment_breakdown?.rider_due
      ?? 0,
  );
  const productsAmount = Number(
    order?.payment_breakdown?.store_due
      ?? Math.max(0, Number(order?.total || 0) - deliveryAmount),
  );
  const reservationReleaseAt = Date.parse(order?.flow_v1?.reservation_release_at || "");
  const reservationSeconds = Number.isFinite(reservationReleaseAt)
    ? Math.max(0, Math.ceil((reservationReleaseAt - clock) / 1000))
    : Number(order?.flow_v1?.reservation_release_seconds_remaining || 0);
  const reservationClock = `${String(Math.floor(reservationSeconds / 60)).padStart(2, "0")}:${String(reservationSeconds % 60).padStart(2, "0")}`;
  const canShare = Boolean(order?.can_share_order) && (isFood ? Boolean(order?.delivery_assigned_to_me) : ["ASSIGNED", "PICKED_UP"].includes(order?.status));
  const shareMessage = order ? buildShareMessage(order, isFood, reference, mapsUrl, {
    products: productsAmount,
    delivery: deliveryAmount,
    collect: collectAmount,
  }) : "";

  return (
    <DriverShell>
      <Link className="page-back" to="/delivery/orders">← Volver a servicios</Link>
      {loading && <div className="food-message"><b>Cargando servicio…</b></div>}
      {error && <div className="food-message food-message--error"><b>{error}</b></div>}
      {order && (
        <div className="driver-detail">
          <header className="driver-order-hero">
            <div><small>{isFood ? "PEDIDO DE COMIDA" : order.order_type_label}</small><h1 className="driver-order-reference">{reference}</h1></div>
            <strong>{money(isFood ? order.net_earning || order.commission : order.moto_net_amount)}<small>GANANCIA APROX.</small></strong>
          </header>
          <section className="driver-route-card">
            <div className="driver-route-point is-pickup"><i>●</i><div><small>RECOJO</small><b>{isFood ? order.restaurant?.name : order.pickup_address}</b><span>{isFood ? order.restaurant?.address : order.pickup_reference}</span></div></div>
            <i />
            <div className="driver-route-point is-dropoff"><i>◆</i><div><small>ENTREGA</small><b>{isFood ? order.address : order.dropoff_address}</b><span>{isFood ? order.landmark : order.dropoff_reference}</span>{customerGps && <span className="driver-gps-value">GPS: {customerGps.lat.toFixed(6)}, {customerGps.lng.toFixed(6)}</span>}</div></div>
            <a href={mapsUrl} target="_blank" rel="noreferrer"><span>⌖</span>{customerGps ? "Abrir GPS y ruta" : "Abrir dirección en mapa"}</a>
          </section>
          {isFood ? (
            <>
              {order.flow_v1?.waiting_store_acceptance && (
                <section className="driver-reservation-wait">
                  <div><small>PEDIDO RESERVADO</small><h2>Esperando aceptación del negocio</h2><p>La orden está separada para ti. Puedes llamar al local para confirmar que la atiendan.</p></div>
                  <strong>{reservationSeconds > 0 ? reservationClock : "YA PUEDES LIBERARLO"}</strong>
                  <OrderActionButton disabled={busy || reservationSeconds > 0} label="Liberar pedido" onComplete={releaseReservation} tone="dark" />
                </section>
              )}
              <div className="driver-contact-grid">
                <Contact title="NEGOCIO" icon="▣" name={order.restaurant_contact?.name || order.restaurant?.name} phone={order.restaurant_contact?.phone} />
                {order.delivery_assigned_to_me && <Contact title="CLIENTE" icon="●" name={order.user?.name} phone={order.user?.phone} />}
              </div>
              <section className="driver-detail-card driver-items-card">
                <div className="driver-card-title"><span>DETALLE DEL PEDIDO</span><b>{order.orderitems?.length || 0} productos</b></div>
                <div className="driver-item-list">
                  {order.orderitems?.map((item) => (
                    <div className="driver-item-line" key={item.id}>
                      <span>{item.quantity}</span>
                      <div className="driver-item-copy">
                        <div className="driver-item-product">
                          <b>{item.name}</b>
                          <strong>{money(Number(item.price || 0) * Number(item.quantity || 1))}</strong>
                        </div>
                        {item.order_item_addons?.map((addon) => (
                          <div className="driver-item-addon" key={addon.id || `${item.id}-${addon.addon_name}`}>
                            <span>+ {addon.addon_name}</span>
                            <strong>{money(Number(addon.addon_price ?? addon.price ?? 0) * Number(item.quantity || 1))}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {String(order.order_comment || "").trim() && (
                  <div className="driver-customer-note">
                    <span aria-hidden="true">📝</span>
                    <div><small>INDICACIÓN DEL CLIENTE</small><b>{order.order_comment}</b></div>
                  </div>
                )}
                <div className="driver-payment-summary">
                  <div><span>Productos</span><b>{money(productsAmount)}</b></div>
                  <div><span>Tarifa de delivery</span><b>{money(deliveryAmount)}</b></div>
                  <div className="is-collect"><span>Total a cobrar</span><b>{money(collectAmount)}</b></div>
                  <div><span>Forma de pago</span><b>{order.payment_display || order.payment_mode}</b></div>
                </div>
                {order.cash_payment_method === "CASH" && Number(order.cash_change_amount) > 0 && (
                  <div className="driver-change-alert"><span>↔</span><div><small>EL CLIENTE NECESITA VUELTO</small><b>Preparar cambio de {money(order.cash_change_amount)}</b></div></div>
                )}
              </section>
              {canShare && (
                <a className="driver-share-order" href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`} target="_blank" rel="noreferrer">
                  <span aria-hidden="true">↗</span><div><b>Compartir pedido por WhatsApp</b><small>Envía contactos, direcciones, productos, cobro y GPS</small></div>
                </a>
              )}
              <div className="driver-primary-actions">
                {!order.delivery_assigned_to_me && [1, 2, 7].includes(Number(order.orderstatus_id)) && <OrderActionButton disabled={busy} label={Number(order.orderstatus_id) === 1 ? "Reservar pedido" : "Aceptar pedido"} onComplete={() => act("/delivery/accept-to-deliver", { order_id: order.id }, true)} />}
                {order.delivery_assigned_to_me && [2, 3, 7].includes(Number(order.orderstatus_id)) && <OrderActionButton disabled={busy} label="Confirmar recojo" onComplete={() => act("/delivery/pickedup-order", { order_id: order.id }, true)} />}
                {Number(order.orderstatus_id) === 4 && (
                  <>
                    {settings.enableDeliveryPin === "true" && <input value={pin} onChange={(event) => setPin(event.target.value.toUpperCase())} placeholder="PIN de entrega" />}
                    <OrderActionButton disabled={busy || (settings.enableDeliveryPin === "true" && !pin.trim())} label="Confirmar entrega" onComplete={() => act("/delivery/deliver-order", { order_id: order.id, delivery_pin: pin }, true)} />
                  </>
                )}
              </div>
              {order.delivery_assigned_to_me && [1, 3, 7].includes(Number(order.orderstatus_id)) && (
                <details className="driver-cancellation-request">
                  <summary>{order.flow_v1?.driver_cancellation_requested ? "Cancelación solicitada" : "¿Hay un problema con el pedido?"}</summary>
                  {order.flow_v1?.driver_cancellation_requested ? (
                    <p>El negocio ya recibió tu solicitud: <b>{order.flow_v1.driver_cancellation_reason}</b></p>
                  ) : (
                    <><p>Habla primero con el cliente o negocio. Luego registra el motivo para que ellos confirmen la cancelación.</p><OrderActionButton disabled={busy} label="Solicitar cancelación" tone="dark" onComplete={() => {
                      const reason = window.prompt("Motivo de la cancelación:", "Negocio cerrado o producto no disponible");
                      if (!reason?.trim()) return Promise.resolve(false);
                      return act("/delivery/request-order-cancellation", { order_id: order.id, reason: reason.trim() }, true);
                    }} /></>
                  )}
                </details>
              )}
            </>
          ) : (
            <>
              {order.status !== "PENDING" && <><Contact title="RECOJO" name={order.pickup_contact_name} phone={order.pickup_contact_phone} /><Contact title="ENTREGA" name={order.dropoff_contact_name} phone={order.dropoff_contact_phone} /></>}
              <section className="driver-detail-card"><small>DETALLES</small><p>{order.package_description}</p><p>{order.distance_km} km · {order.duration_min} min · Total {money(order.total)}</p>{order.customer_notes && <p><b>Nota:</b> {order.customer_notes}</p>}</section>
              {canShare && (
                <a className="driver-share-order" href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`} target="_blank" rel="noreferrer">
                  <span aria-hidden="true">↗</span><div><b>Compartir servicio por WhatsApp</b><small>Envía contactos, direcciones, encargo y GPS</small></div>
                </a>
              )}
              <div className="driver-primary-actions">
                {["PENDING", "CONFIRMED"].includes(order.status) && <OrderActionButton disabled={busy} label={`Aceptar ${kind === "taxi" ? "viaje" : "envío"}`} onComplete={() => act(`/delivery/service-orders/${order.id}/accept`, {}, true)} />}
                {order.status === "ASSIGNED" && <OrderActionButton disabled={busy} label={kind === "taxi" ? "Iniciar viaje" : "Confirmar recojo"} onComplete={() => act(`/delivery/service-orders/${order.id}/pickup`, {}, true)} />}
                {order.status === "PICKED_UP" && <OrderActionButton disabled={busy} label={kind === "taxi" ? "Finalizar viaje" : "Confirmar entrega"} onComplete={() => act(`/delivery/service-orders/${order.id}/deliver`, {}, true)} />}
              </div>
            </>
          )}
        </div>
      )}
    </DriverShell>
  );
}
